/**
 * Peragon review list: the paginated reviews on a PDP or the reviews page.
 *
 * Depends on peragon-reviews-summary.js (loaded alongside it by
 * sections/peragon-reviews.liquid), which owns the star renderer, the number
 * formatting and the shared summary cache. Every rating badge on the site --
 * this section's summary block, the fitment configurator's row, anything added
 * later -- draws stars from that one implementation, and this list publishes the
 * rollup it fetches so those badges get real numbers without a second request.
 */
(function () {
  var ATTRIBUTE_BARS = [
    { key: 'customer_support_rating', label: 'Customer Support' },
    { key: 'ease_of_use_rating', label: 'Ease of Use' },
    { key: 'durability_rating', label: 'Strength and Durability' },
    { key: 'installation_rating', label: 'Ease of Installation' }
  ];

  //Resolved per call rather than once at load: both scripts are deferred, and
  //nothing guarantees which of the two executes first.
  function shared() {
    return window.PeragonReviews || null;
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatBody(value) {
    return escapeHtml(value).replace(/\r\n|\r|\n/g, '<br>');
  }

  function formatCount(value) {
    var api = shared();
    if (api) return api.formatCount(value);
    var number = Number(value);
    if (!isFinite(number)) return String(value || '0');
    return Math.round(number).toLocaleString('en-US');
  }

  function formatProductBadge(product) {
    var name = String(product || '').trim();
    if (!name) return '';
    return /™|\btm$/i.test(name) ? name : name + '™';
  }

  function clampPercent(value) {
    var number = Number(value);
    if (!isFinite(number)) return null;
    return Math.max(0, Math.min(100, number));
  }

  function starMarkup(rating, className) {
    var api = shared();
    if (!api) {
      console.warn('[peragon-reviews] peragon-reviews-summary.js is not loaded; stars omitted.');
      return '';
    }
    return api.starMarkup(rating, className);
  }

  function attributeBarsMarkup(review) {
    var bars = ATTRIBUTE_BARS.map(function (item) {
      var percent = clampPercent(review[item.key]);
      if (percent === null) return '';
      return (
        '<div class="peragon-review__bar">' +
          '<span class="peragon-review__bar-label">' + escapeHtml(item.label) + '</span>' +
          '<span class="peragon-review__bar-track" aria-hidden="true">' +
            '<span class="peragon-review__bar-fill" style="width:' + percent + '%"></span>' +
          '</span>' +
          '<span class="sr-only">' + escapeHtml(item.label) + ': ' + percent + '%</span>' +
        '</div>'
      );
    }).filter(Boolean);

    if (!bars.length) return '';
    return '<div class="peragon-review__ratings">' + bars.join('') + '</div>';
  }

  function photosMarkup(photos) {
    if (!Array.isArray(photos) || !photos.length) return '';
    return (
      '<div class="peragon-review__photos">' +
        photos.map(function (photo) {
          var thumb = photo && (photo.thumb || photo.full);
          var full = photo && (photo.full || photo.thumb);
          if (!thumb) return '';
          var title = (photo && photo.title) || 'Customer photo';
          return (
            '<button type="button" class="peragon-review__photo" data-full="' + escapeHtml(full) + '" aria-label="' + escapeHtml(title) + '">' +
              '<img src="' + escapeHtml(thumb) + '" alt="' + escapeHtml(title) + '" width="150" loading="lazy">' +
            '</button>'
          );
        }).join('') +
      '</div>'
    );
  }

  function normalizeReplies(review) {
    var replies = [];

    function push(item) {
      if (!item) return;
      if (typeof item === 'string') {
        if (item.trim()) replies.push({ author: '', content: item });
        return;
      }
      var author = item.author || item.reply_author || item.name || '';
      var content = item.content || item.reply_content || item.body || item.message || '';
      if (String(content).trim()) replies.push({ author: author, content: content });
    }

    if (Array.isArray(review.replies)) review.replies.forEach(push);
    if (Array.isArray(review.comments)) review.comments.forEach(push);
    if (review.reply) push(review.reply);
    if (review.response) push(review.response);
    if (review.reply_content) push({ author: review.reply_author, content: review.reply_content });
    return replies;
  }

  function repliesMarkup(review) {
    var replies = normalizeReplies(review);
    if (!replies.length) return '';
    return (
      '<div class="peragon-review__comments">' +
        replies.map(function (reply) {
          return (
            '<div class="peragon-review__reply">' +
              (reply.author ? '<h3 class="peragon-review__reply-author">' + escapeHtml(reply.author) + '</h3>' : '') +
              '<p class="peragon-review__reply-content">' + formatBody(reply.content) + '</p>' +
            '</div>'
          );
        }).join('') +
      '</div>'
    );
  }

  function reviewMarkup(review) {
    var badge = formatProductBadge(review.product);
    return (
      '<article class="peragon-review" role="listitem">' +
        '<div class="peragon-review__main">' +
          (review.created ? '<p class="peragon-review__date">' + escapeHtml(review.created) + '</p>' : '') +
          (review.author ? '<h3 class="peragon-review__author">' + escapeHtml(review.author) + '</h3>' : '') +
          (review.truck ? '<p class="peragon-review__truck">' + escapeHtml(review.truck) + '</p>' : '') +
          '<div class="peragon-review__meta">' +
            starMarkup(review.rating, 'peragon-review-stars--item') +
            (badge ? '<span class="peragon-review__badge">' + escapeHtml(badge) + '</span>' : '') +
          '</div>' +
          (review.content ? '<p class="peragon-review__body">' + formatBody(review.content) + '</p>' : '') +
          photosMarkup(review.photos) +
        '</div>' +
        attributeBarsMarkup(review) +
        repliesMarkup(review) +
      '</article>'
    );
  }

  //Review JSON-LD mirrors the reviews on screen -- every one on the current page,
  //rewritten when the shopper pages -- since Google wants marked-up reviews visible.
  var lineSchemas = {};  //reporting_category_id -> { node, json } on the reviews pages

  function isoDate(value) {
    var date = new Date(value);
    if (!value || isNaN(date.getTime())) return '';
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function reviewSchema(review) {
    var rating = Number(review.rating);
    var body = String(review.content || '').trim();
    //The author's first name, as the old markup used -- "author" reads "William from California".
    var author = String(review.first_name || review.author || '').trim();
    //Author and rating are required by Google; the text is not.
    if (!(rating > 0) || !author) return null;

    var item = {
      '@type': 'Review',
      author: { '@type': 'Person', name: author },
      reviewRating: { '@type': 'Rating', ratingValue: rating, bestRating: 5, worstRating: 1 }
    };
    if (body) item.reviewBody = body;
    var date = isoDate(review.created);
    if (date) item.datePublished = date;
    return item;
  }

  function writeJsonLd(node, json) {
    node.textContent = JSON.stringify(json);
  }

  function setReviews(json, reviews) {
    if (reviews.length) json.review = reviews;
    else delete json.review;
  }

  function addReviewsTo(node, reviews) {
    try {
      var json = JSON.parse(node.textContent);
      setReviews(json, reviews);
      writeJsonLd(node, json);
    } catch (err) {
      console.warn('[peragon-reviews] could not add reviews to product JSON-LD.', err);
    }
  }

  function lineProductSchema(id, line, reviews) {
    var existing = lineSchemas[id];
    if (existing) {
      setReviews(existing.json, reviews);
      writeJsonLd(existing.node, existing.json);
      return;
    }

    var json = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: line.name,
      brand: { '@type': 'Brand', name: line.brand || 'Peragon' }
    };
    setReviews(json, reviews);
    if (line.url) json.url = line.url;
    if (line.image) json.image = line.image;
    if (line.lowPrice && line.highPrice) {
      json.offers = {
        '@type': 'AggregateOffer',
        lowPrice: line.lowPrice,
        highPrice: line.highPrice,
        priceCurrency: line.priceCurrency || 'USD'
      };
    }

    var node = document.createElement('script');
    node.type = 'application/ld+json';
    node.setAttribute('data-review-schema', id);
    writeJsonLd(node, json);
    document.head.appendChild(node);
    lineSchemas[id] = { node: node, json: json };

    //The line's own rollup, not this list's: the reviews page lists both lines.
    var api = shared();
    if (!api) return;
    api.load(id).then(function (summary) {
      var rating = summary && Number(summary.rating);
      var count = summary && Number(summary.count);
      if (!(rating > 0) || !(count > 0)) return;
      json.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: rating,
        ratingCount: Math.round(count),
        reviewCount: Math.round(count),
        bestRating: 5,
        worstRating: 1
      };
      writeJsonLd(node, json);
    });
  }

  function publishReviewSchema(root, reviews, defaultCategoryId) {
    var configNode = root.querySelector('[data-review-schema-config]');
    var config = {};
    try {
      config = configNode ? JSON.parse(configNode.textContent) : {};
    } catch (err) {
      console.warn('[peragon-reviews] review schema config is not valid JSON.', err);
    }

    var byLine = {};
    reviews.forEach(function (review) {
      var id = String(review.reporting_category_id || defaultCategoryId || '');
      var item = id && reviewSchema(review);
      if (!item) return;
      byLine[id] = byLine[id] || [];
      byLine[id].push(item);
    });

    //A page about one product never grows a second one: reviews of another line
    //(the API widens a truck's list when it is short) are just left out.
    var hosts = document.querySelectorAll('script[type="application/ld+json"][data-review-category]');
    if (hosts.length) {
      Array.prototype.forEach.call(hosts, function (node) {
        addReviewsTo(node, byLine[node.getAttribute('data-review-category')] || []);
      });
      return;
    }

    //Reviews pages: a line without reviews on this page keeps its rating and
    //offers but drops the previous page's reviews.
    Object.keys(config).forEach(function (id) {
      if (!config[id] || !config[id].name) return;
      if (byLine[id] || lineSchemas[id]) lineProductSchema(id, config[id], byLine[id] || []);
    });
  }

  function paginationMarkup(current, totalPages) {
    current = Math.max(1, Number(current) || 1);
    totalPages = Math.max(1, Number(totalPages) || 1);
    if (totalPages <= 1) return '';

    var windowSize = 9;
    var start = Math.max(1, current - Math.floor(windowSize / 2));
    var end = start + windowSize - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - windowSize + 1);
    }

    function item(label, pageNum, options) {
      options = options || {};
      if (options.current) {
        return '<li class="is-active"><span aria-current="page">' + escapeHtml(label) + '</span></li>';
      }
      if (options.disabled) {
        return '<li class="is-disabled"><span>' + escapeHtml(label) + '</span></li>';
      }
      return '<li><a href="#page=' + pageNum + '" data-page="' + pageNum + '">' + escapeHtml(label) + '</a></li>';
    }

    var html = '<ul class="peragon-reviews-pagination__list">';
    html += item('First', 1, { disabled: current <= 1 });
    html += item('Previous', current - 1, { disabled: current <= 1 });
    for (var n = start; n <= end; n++) {
      html += item(String(n), n, { current: n === current });
    }
    html += item('Next', current + 1, { disabled: current >= totalPages });
    html += item('Last', totalPages, { disabled: current >= totalPages });
    html += '</ul>';
    return html;
  }

  function ensureLightbox() {
    var existing = document.querySelector('.peragon-reviews-lightbox');
    if (existing) return existing;

    var lightbox = document.createElement('div');
    lightbox.className = 'peragon-reviews-lightbox';
    lightbox.hidden = true;
    lightbox.innerHTML =
      '<button type="button" class="peragon-reviews-lightbox__close" aria-label="Close photo">&times;</button>' +
      '<img class="peragon-reviews-lightbox__image" alt="">';
    document.body.appendChild(lightbox);

    function close() {
      lightbox.hidden = true;
      lightbox.querySelector('img').src = '';
      document.body.classList.remove('peragon-reviews-lightbox-open');
    }

    lightbox.addEventListener('click', function (event) {
      if (event.target === lightbox || event.target.closest('.peragon-reviews-lightbox__close')) {
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !lightbox.hidden) close();
    });

    return lightbox;
  }

  function openLightbox(src, alt) {
    var lightbox = ensureLightbox();
    var image = lightbox.querySelector('img');
    image.src = src;
    image.alt = alt || 'Customer photo';
    lightbox.hidden = false;
    document.body.classList.add('peragon-reviews-lightbox-open');
  }

  function createReviewsList(root) {
    var list = root.querySelector('[data-reviews-list]');

    //Every other node below is optional and guarded, but the list is what this
    //controller exists to fill, so there is nothing to boot without it. It used to
    //be assumed: a [data-peragon-reviews] element with no list inside threw on the
    //first addEventListener, and because that throw happened inside the
    //querySelectorAll().forEach() that starts every list on the page, it also took
    //out the real review list if the malformed one came first in the document.
    //
    //data-peragon-reviews is this controller's boot hook, not a "reviews live here"
    //marker -- a rating badge wants data-reviews-summary-target instead
    //(see snippets/reviews-summary-badge.liquid).
    if (!list) {
      console.warn('[peragon-reviews] [data-peragon-reviews] element has no ' +
        '[data-reviews-list] inside it, skipping.', root);
      return;
    }

    var status = root.querySelector('[data-reviews-status]');
    var footer = root.querySelector('[data-reviews-footer]');
    var pagination = root.querySelector('[data-reviews-pagination]');
    var summary = root.querySelector('[data-reviews-summary]');
    var summaryStars = root.querySelector('[data-summary-stars]');
    var summaryRating = root.querySelector('[data-summary-rating]');
    var apiUrl = root.getAttribute('data-api-url') || 'https://admin.peragon.com/shopify/reviews';
    var fallbackUrl = (root.getAttribute('data-fallback-url') || '').trim();
    var categoryId = (root.getAttribute('data-reporting-category-id') || '').trim();
    var showSummary = root.getAttribute('data-show-summary') !== 'false';
    var loadingLabel = root.getAttribute('data-loading-label') || 'Loading…';
    var pageLimit = Number(root.getAttribute('data-page-limit')) || 20;
    //Truck cover pages: only that truck's reviews (the API widens below data-min).
    var truckMake = (root.getAttribute('data-make') || '').trim();
    var truckModel = (root.getAttribute('data-model') || '').trim();
    var truckMin = (root.getAttribute('data-min') || '').trim();
    var page = 1;
    var totalPages = 1;
    var loading = false;

    function setStatus(message, isError) {
      if (!status) return;
      if (!message) {
        status.hidden = true;
        status.textContent = '';
        status.classList.remove('is-error');
        return;
      }
      status.hidden = false;
      status.textContent = message;
      status.classList.toggle('is-error', Boolean(isError));
    }

    function setLoading(isLoading) {
      loading = isLoading;
      if (pagination) pagination.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      root.classList.toggle('is-loading', isLoading);
    }

    /**
     * Hand this page's rollup to the shared layer so every rating badge on the
     * page shows it, then paint this section's own summary block.
     *
     * The publish deliberately sits ABOVE the showSummary / summary guards: the
     * Landmark PDP sets show_summary false and has no summary block, so anything
     * behind those guards never ran there -- which is exactly the page whose
     * fitment badge needs the numbers.
     */
    function renderSummary(payload) {
      var rating = payload.review_rating;
      var count = payload.review_count != null ? payload.review_count : payload.total;

      var api = shared();
      //A truck-filtered list is one truck's reviews, not the product line's, so it must
      //not repaint the line-wide badges (they load their own rollup).
      //Same for this section's own summary, labelled as the whole line's rating.
      if (truckMake) return;
      if (api) api.publish(categoryId, { rating: rating, count: count });

      if (!showSummary || !summary) return;
      if (rating == null || count == null) return;

      if (summaryStars) summaryStars.innerHTML = starMarkup(rating, 'peragon-review-stars--summary');
      if (summaryRating) {
        summaryRating.textContent = api
          ? api.summaryText(rating, count, categoryId)
          : String(rating) + ' out of ' + formatCount(count) + ' reviews';
      }
      summary.hidden = false;
    }

    function categoryIds() {
      var ids = String(categoryId || '').split(',').map(function (id) {
        return id.trim();
      }).filter(Boolean);
      return ids;
    }

    function fallbackKey() {
      var ids = categoryIds();
      return ids.length === 1 ? ids[0] : 'all';
    }

    // Combined list when no single category: /shopify/reviews?page=3&limit=20
    function reviewsUrl(pageNum) {
      var url = apiUrl + '?page=' + encodeURIComponent(pageNum) + '&limit=' + encodeURIComponent(pageLimit);
      var ids = categoryIds();
      if (ids.length === 1) {
        url += '&reporting_category_id=' + encodeURIComponent(ids[0]);
      }
      if (truckMake) {
        url += '&make=' + encodeURIComponent(truckMake);
        if (truckModel) url += '&model=' + encodeURIComponent(truckModel);
        if (truckMin) url += '&min=' + encodeURIComponent(truckMin);
      }
      return url;
    }

    function fetchLivePage(pageNum) {
      return fetch(reviewsUrl(pageNum)).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      }).then(function (payload) {
        return [payload];
      });
    }

    function corsHelpMessage() {
      var origin = window.location.origin;
      if (/\.shopifypreview\.com$/i.test(window.location.hostname)) {
        return 'Theme preview is blocked by CakePHP CORS. admin.peragon.com currently allows https://0biceq-ge.myshopify.com, not ' + origin + '. Allow *.shopifypreview.com (and https://www.peragon.com for the live domain) on the /shopify/* API.';
      }
      return 'admin.peragon.com did not allow origin ' + origin + '. Add this domain to the CakePHP CORS allowlist for /shopify/reviews.';
    }

    function fetchFallbackPayloads(pageNum) {
      if (!fallbackUrl) return Promise.reject(new Error('No fallback reviews URL'));
      if (Number(pageNum) !== 1) return Promise.reject(new Error('Fallback only includes page 1'));
      return fetch(fallbackUrl).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      }).then(function (data) {
        var payload = data && data[fallbackKey()];
        if (!payload) throw new Error('Fallback reviews were empty');
        return [payload];
      });
    }

    function mergePayloads(payloads) {
      var reviews = [];
      var seen = {};
      var totalCount = 0;
      var ratingWeight = 0;
      var pages = 1;

      payloads.forEach(function (payload) {
        if (!payload) return;
        var payloadPages = Number(payload.total_pages);
        if (isFinite(payloadPages) && payloadPages > pages) pages = payloadPages;
        var count = Number(payload.review_count != null ? payload.review_count : payload.total);
        var rating = Number(payload.review_rating);
        if (isFinite(count) && count > 0) {
          totalCount += count;
          if (isFinite(rating)) ratingWeight += rating * count;
        }
        ((payload.reviews) || []).forEach(function (review) {
          if (!review || seen[review.id]) return;
          seen[review.id] = true;
          reviews.push(review);
        });
      });

      if (payloads.length === 1) {
        reviews = (payloads[0] && payloads[0].reviews) || reviews;
      } else {
        reviews.sort(function (a, b) {
          return Number(b.id || 0) - Number(a.id || 0);
        });
      }

      return {
        total_pages: pages,
        review_count: totalCount || null,
        review_rating: totalCount && ratingWeight ? (ratingWeight / totalCount).toFixed(1) : null,
        reviews: reviews
      };
    }

    function renderPagination() {
      if (!pagination) return;
      pagination.innerHTML = paginationMarkup(page, totalPages);
      if (footer) footer.hidden = totalPages <= 1;
    }

    function pageFromHash() {
      var match = String(window.location.hash || '').match(/page=(\d+)/i);
      if (!match) return 1;
      return Math.max(1, parseInt(match[1], 10) || 1);
    }

    function fetchPage(nextPage, options) {
      options = options || {};
      if (loading) return Promise.resolve();
      nextPage = Math.max(1, Number(nextPage) || 1);
      setLoading(true);
      setStatus(loadingLabel);

      return fetchLivePage(nextPage)
        .catch(function (err) {
          console.warn('Peragon reviews API blocked from this origin, using theme fallback.', err);
          return fetchFallbackPayloads(nextPage);
        })
        .then(function (payloads) {
          var payload = mergePayloads(payloads);
          var reviews = payload.reviews || [];
          list.innerHTML = reviews.map(reviewMarkup).join('');
          page = nextPage;
          totalPages = payload.total_pages || 1;
          renderSummary(payload);
          renderPagination();
          publishReviewSchema(root, reviews, categoryIds().length === 1 ? categoryIds()[0] : '');
          setStatus(reviews.length ? '' : 'No reviews found.');
          if (options.scroll && root.scrollIntoView) {
            root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          if (options.updateHash !== false && window.history && window.history.replaceState) {
            //Silent -- paging reviews is not a navigation. See snippets/native-history.liquid.
            theme.replaceStateSilent(null, '', '#page=' + page);
          }
        })
        .catch(function (err) {
          console.error('Peragon reviews failed', err);
          if (!list.children.length) setStatus(corsHelpMessage(), true);
          else setStatus('');
          if (footer && !list.children.length) footer.hidden = true;
        })
        .finally(function () {
          setLoading(false);
        });
    }

    if (pagination) {
      pagination.addEventListener('click', function (event) {
        var link = event.target.closest('[data-page]');
        if (!link || !pagination.contains(link)) return;
        event.preventDefault();
        var nextPage = Number(link.getAttribute('data-page'));
        if (!nextPage || nextPage === page || loading) return;
        fetchPage(nextPage, { scroll: true });
      });
    }

    list.addEventListener('click', function (event) {
      var photo = event.target.closest('.peragon-review__photo');
      if (!photo || !list.contains(photo)) return;
      var src = photo.getAttribute('data-full');
      if (!src) return;
      var image = photo.querySelector('img');
      openLightbox(src, image ? image.alt : 'Customer photo');
    });

    fetchPage(pageFromHash(), { scroll: false, updateHash: false });
  }

  function reviewsScrollTarget() {
    return document.querySelector('.pdp-reviews-header')
      || document.getElementById('reviews')
      || document.querySelector('[data-peragon-reviews]');
  }

  function scrollToReviews() {
    var target = reviewsScrollTarget();
    if (!target || !target.scrollIntoView) return false;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }

  //Anything that reads as "N reviews" scrolls to the list. Class-based so it
  //matches every badge on the page, not just the first one an id could name;
  //#reviews-small-display stays in the list for any markup not yet migrated.
  //
  //The Klaviyo selectors that used to be here are gone with its widgets -- the
  //Peragon admin is the only source of review data now.
  function reviewsJumpTrigger(node) {
    if (!node || !node.closest) return null;
    return node.closest([
      '[data-reviews-summary-target]',
      '.reviews-small-display',
      '#reviews-small-display',
      'a[href="#reviews"]'
    ].join(','));
  }

  function bindReviewsJump() {
    document.addEventListener('click', function (event) {
      var trigger = reviewsJumpTrigger(event.target);
      if (!trigger) return;
      if (trigger.closest('[data-peragon-reviews]')) return;
      if (!reviewsScrollTarget()) return;
      event.preventDefault();
      event.stopPropagation();
      scrollToReviews();
    }, true);

    if (window.location.hash === '#reviews') {
      window.setTimeout(scrollToReviews, 50);
    }
  }

  /**
   * Owner-photo strip on the truck cover pages (sections/truck-owner-photos.liquid):
   * photos from reviews by owners of the page's truck, the way the old /shop/covers
   * pages showed them (ReviewsCell::photos). The API widens model -> make -> all when
   * fewer than data-min reviews with photos match. Hides itself if nothing comes back.
   */
  function createOwnerPhotos(root) {
    var grid = root.querySelector('[data-owner-photos-grid]');
    if (!grid) return;

    var max = Number(root.getAttribute('data-max')) || 12;
    var url = (root.getAttribute('data-api-url') || 'https://admin.peragon.com/shopify/reviews') +
      '?page=1&limit=20&with_photos=1';
    ['reporting-category-id', 'make', 'model', 'min'].forEach(function (name) {
      var value = (root.getAttribute('data-' + name) || '').trim();
      if (value) url += '&' + name.replace(/-/g, '_') + '=' + encodeURIComponent(value);
    });

    fetch(url).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    }).then(function (payload) {
      var items = [];
      ((payload && payload.reviews) || []).forEach(function (review) {
        (review.photos || []).forEach(function (photo) {
          if (items.length >= max || !photo || !(photo.thumb || photo.full)) return;
          //Photo titles are camera filenames ("IMG_3734.jpeg"); the truck is the useful alt.
          var alt = (review.truck ? review.truck + ' ' : '') + 'owner photo' +
            (review.author ? ' from ' + review.author : '');
          items.push(
            '<button type="button" class="peragon-review__photo" data-full="' + escapeHtml(photo.full || photo.thumb) + '" aria-label="' + escapeHtml(alt) + '">' +
              '<img src="' + escapeHtml(photo.thumb || photo.full) + '" alt="' + escapeHtml(alt) + '" loading="lazy">' +
            '</button>'
          );
        });
      });
      if (!items.length) return;
      grid.innerHTML = items.join('');
      root.hidden = false;
    }).catch(function (err) {
      console.warn('[peragon-reviews] owner photos unavailable.', err);
    });

    grid.addEventListener('click', function (event) {
      var photo = event.target.closest('.peragon-review__photo');
      if (!photo || !grid.contains(photo)) return;
      var image = photo.querySelector('img');
      openLightbox(photo.getAttribute('data-full'), image ? image.alt : 'Customer photo');
    });
  }

  //The truck page content block's photo strip opens the same lightbox.
  window.PeragonReviewsLightbox = openLightbox;

  ready(function () {
    bindReviewsJump();
    document.querySelectorAll('[data-peragon-reviews]').forEach(createReviewsList);
    document.querySelectorAll('[data-owner-photos]').forEach(createOwnerPhotos);
  });
})();
